// RelatedWorkPanel — the most related PRIOR sessions for the trace in view,
// with the evidence shown verbatim.
//
// Consumer + decision: an analyst reading one session, deciding "is this part
// of a workstream I've already seen, or something new?" A suggestion is
// EVIDENCE-based — the two sessions touched the same specific files — never a
// bare similarity score: each row expands to the exact shared paths, so the
// suggestion is instantly checkable. Precision-first: the gates (suffix
// depth >= 3, self-calibrated fanout band — see lib/relatedWork/gates.js)
// keep suggestions trustworthy at the cost of recall, so the empty state
// must never read as "no related work exists".
//
// Scoring is in-browser, but the SEARCH is not: lib/relatedWork/queries.js
// asks the server for just this session's anchor neighbourhood, so there is no
// tenant-wide corpus held in the tab.
import { useEffect, useMemo, useRef, useState } from "react";
import HoverTooltip from "./HoverTooltip.jsx";
import { CONTEXT_EYEBROW_CLASS } from "./SplitPage.jsx";
import { useLabel } from "../lib/dimensionDefs.js";
import { getSessionPromptPreviews } from "../lib/sessionLabels.js";
import {
    buildAnchorIndex,
    rankCandidateSessions,
    rankNeedles,
    scoreRelated,
} from "../lib/relatedWork/gates.js";
import {
    fetchAnchorNeighbourhood,
    fetchSessionCount,
    fetchSessionMeta,
    fetchSessionPaths,
    lookbackTimeRange,
    RELATED_WORK_LIMITS,
} from "../lib/relatedWork/queries.js";

function relTime(ms) {
    const delta = Date.now() - ms;
    if (!Number.isFinite(delta)) return "";
    const mins = Math.round(delta / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.round(hours / 24)}d ago`;
}

function shortId(id) {
    return String(id ?? "").slice(0, 8);
}

function SharedAnchorRow({ anchor, sessionCount }) {
    return (
        <div className="flex items-baseline gap-[8px] min-w-0">
            <HoverTooltip
                wide
                placement="top"
                label={
                    <span className="grid gap-[4px] py-[1px]">
                        <span className="text-[color:var(--muted)]">Both traces touched</span>
                        <span className="font-mono text-[10px] leading-[1.35] break-all">{anchor.queryPath}</span>
                        {anchor.candidatePath !== anchor.queryPath && (
                            <span className="font-mono text-[10px] leading-[1.35] break-all text-[color:var(--muted)]">
                                there: {anchor.candidatePath}
                            </span>
                        )}
                        <span className="text-[color:var(--muted)]">
                            {anchor.fanout} of {sessionCount} traces touched this path. Rarity
                            score: ln({sessionCount}/{anchor.fanout}) = +{anchor.weight.toFixed(1)}.
                        </span>
                    </span>
                }
            >
                <span className="min-w-0 flex-1 cursor-help truncate font-mono text-[10px] leading-[1.5] text-[color:var(--ink)]">
                    {anchor.queryPath}
                </span>
            </HoverTooltip>
            <span className="shrink-0 text-[9px] leading-[1.5] tabular-nums text-[color:var(--muted)]">
                {anchor.fanout}/{sessionCount} · +{anchor.weight.toFixed(1)}
            </span>
        </div>
    );
}

const ANCHOR_PREVIEW_COUNT = 6;

function SuggestionRow({ suggestion, label, sessionCount, onOpen }) {
    const [expanded, setExpanded] = useState(false);
    const endpointLabel = useLabel(suggestion.endpointId ? { dim: "endpoints", id: suggestion.endpointId } : null);
    const title = label || shortId(suggestion.sessionId);
    const canOpen = typeof onOpen === "function";
    return (
        <div className="grid gap-[4px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[8px] py-[6px] min-w-0">
            <div className="flex items-center gap-[6px] min-w-0">
                <button
                    type="button"
                    onClick={onOpen}
                    disabled={!canOpen}
                    title={canOpen ? "Open this prior trace" : "Prior trace unavailable"}
                    className={[
                        "min-w-0 flex-1 truncate text-left text-[12px] leading-[1.3] text-[color:var(--ink)]",
                        canOpen ? "cursor-pointer hover:underline" : "cursor-default opacity-60",
                    ].join(" ")}
                >
                    {title}
                </button>
                {suggestion.sameThread && (
                    <HoverTooltip placement="top" label="Active within 7 days of this trace. Likely part of the same workstream.">
                        <span className="shrink-0 cursor-help rounded-[2px] border border-[color:var(--line)] px-[5px] py-[1px] text-[9px] leading-[1.2] tracking-[0.06em] uppercase text-[color:var(--muted)]">
                            same thread
                        </span>
                    </HoverTooltip>
                )}
                <span className="shrink-0 text-[10px] leading-[1.2] text-[color:var(--muted)]">{relTime(suggestion.tsMaxMs)}</span>
            </div>
            <div className="flex items-center gap-[8px] min-w-0">
                {endpointLabel && (
                    <span className="min-w-0 truncate text-[10px] leading-[1.2] text-[color:var(--muted)]">{endpointLabel}</span>
                )}
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    className="shrink-0 text-[10px] leading-[1.2] text-[color:var(--muted)] cursor-pointer transition-colors hover:text-[color:var(--ink)]"
                >
                    {suggestion.shared.length} shared {suggestion.shared.length === 1 ? "artifact" : "artifacts"} {expanded ? "▾" : "▸"}
                </button>
            </div>
            {expanded && (
                <div className="grid gap-[1px] border-l border-[color:var(--line-soft)] pl-[8px] min-w-0">
                    <span className="text-[9px] leading-[1.6] tracking-[0.05em] uppercase text-[color:var(--muted-soft)]">
                        score {suggestion.score.toFixed(1)} · {suggestion.shared.length} path{suggestion.shared.length === 1 ? "" : "s"}, weighted by rarity
                    </span>
                    {suggestion.shared.slice(0, ANCHOR_PREVIEW_COUNT).map((a) => (
                        <SharedAnchorRow key={a.key} anchor={a} sessionCount={sessionCount} />
                    ))}
                    {suggestion.shared.length > ANCHOR_PREVIEW_COUNT && (
                        <span className="text-[10px] leading-[1.5] text-[color:var(--muted)]">
                            +{suggestion.shared.length - ANCHOR_PREVIEW_COUNT} more
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

/// The "how matching works" tooltip, exported so WorkTabs can hang it off its
/// shared tab strip (the band copy needs this panel's reported state).
export function relatedWorkTooltip(state) {
    return (
        <span className="grid gap-[6px] py-[2px]">
            <span className="font-medium">Matching</span>
            <span className="text-[color:var(--muted)]">
                Suggestions are earlier traces that touched the same files. Expand a
                row to review the matching paths.
            </span>
            <span className="text-[color:var(--muted)]">
                Eligible paths are files at least three segments deep that appear in
                two or more traces. Common paths are excluded{state.status === "ready" && state.bandMax > 0
                    ? `; paths shared by 2–${state.bandMax} of ${state.sessionCount} traces qualify`
                    : ""}.
            </span>
            <span className="text-[color:var(--muted)]">
                Rare shared paths carry more weight. Results rank by total weight, then
                recency. Same thread marks activity within seven days.
            </span>
        </span>
    );
}

// variant "section" renders the standalone bordered section this panel has
// always been; "tab" renders only the status/body content for a parent-owned
// tab strip (WorkTabs), which follows this panel's state through onStatus.
export default function RelatedWorkPanel({ trace, onOpenTrace, variant = "section", onStatus = null }) {
    const sessionId = trace?.sessionId ? String(trace.sessionId) : null;
    const traceTsMs = trace?.timestamp instanceof Date ? trace.timestamp.getTime() : Number(trace?.timestamp) || Date.now();
    const [state, setState] = useState({ status: "idle" });
    const runIdRef = useRef(0);

    useEffect(() => { onStatus?.(state); }, [onStatus, state]);

    useEffect(() => {
        if (!sessionId) { setState({ status: "hidden" }); return undefined; }
        const runId = ++runIdRef.current;
        let alive = true;
        setState({ status: "loading" });
        (async () => {
            const [sessionCount, pathRows] = await Promise.all([
                fetchSessionCount(),
                fetchSessionPaths(sessionId, traceTsMs),
            ]);
            const ranked = rankNeedles(pathRows);
            const needles = ranked.slice(0, RELATED_WORK_LIMITS.MAX_NEEDLES);
            const anchorKeys = new Set(needles.map((n) => n.key));
            const { pairs, truncated } = await fetchAnchorNeighbourhood(needles.map((n) => n.suffix));
            // Metadata is an IN-list read, so the neighbourhood is narrowed to
            // its best-overlapping candidates first. Overlap only chooses who to
            // look up; the suggestion order is scoreRelated's alone.
            const candidates = rankCandidateSessions(pairs, anchorKeys, sessionId);
            const sessions = await fetchSessionMeta([sessionId, ...candidates]);
            const index = buildAnchorIndex(pairs, { sessionCount, anchorKeys });
            const queryPaths = pathRows.map((r) => r.path);
            const queryAnchorCount = [...anchorKeys].filter((k) => index.anchors.has(k)).length;
            const suggestions = scoreRelated({
                index,
                querySessionId: sessionId,
                queryPaths,
                sessions,
                queryTsMs: traceTsMs,
            });
            const previews = await getSessionPromptPreviews(suggestions.map((s) => s.sessionId), lookbackTimeRange());
            if (!alive || runId !== runIdRef.current) return;
            setState({
                status: "ready",
                suggestions,
                previews,
                queryAnchorCount,
                truncated: truncated || ranked.length > needles.length,
                sessionCount: index.sessionCount,
                bandMax: index.bandMax,
            });
        })().catch((error) => {
            if (!alive || runId !== runIdRef.current) return;
            console.warn("[related-work] unavailable:", error?.message || error);
            setState({ status: "error" });
        });
        return () => { alive = false; };
        // traceTsMs is derived from the same trace selection sessionId keys on.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId]);

    const body = useMemo(() => {
        if (state.status !== "ready") return null;
        if (!state.suggestions.length) {
            return (
                <div className="grid gap-[2px]">
                    <span className="text-[12px] leading-[1.3] text-[color:var(--ink)]">No related prior work surfaced.</span>
                    <span className="text-[10px] leading-[1.35] text-[color:var(--muted)]">
                        {state.queryAnchorCount === 0
                            ? "This trace touched too few specific files to match. Other related work may exist."
                            : "Matches require shared files. Other related work may exist."}
                    </span>
                </div>
            );
        }
        return (
            <div className="grid gap-[6px] min-w-0">
                {state.suggestions.map((s) => (
                    <SuggestionRow
                        key={s.sessionId}
                        suggestion={s}
                        label={state.previews?.get(s.sessionId)?.label || null}
                        sessionCount={state.sessionCount}
                        onOpen={onOpenTrace && state.previews?.get(s.sessionId)?.traceId
                            ? () => onOpenTrace(state.previews.get(s.sessionId).traceId)
                            : null}
                    />
                ))}
                {state.truncated && (
                    <span className="text-[10px] leading-[1.3] text-[color:var(--muted)]">
                        This trace exceeds the search limit. Some matches may be missing.
                    </span>
                )}
            </div>
        );
    }, [state, onOpenTrace]);

    if (state.status === "hidden" || state.status === "idle") return null;
    // A failed read says so. Rendering null instead makes a broken query
    // indistinguishable from a disabled feature or a session with no related
    // work — the panel simply vanishes and nobody can tell why (the detail is
    // in the console line logged above, but only if you know to look).

    const content = state.status === "loading" ? (
        <span className="inline-flex items-center gap-[6px] text-[11px] leading-[1.2] text-[color:var(--muted)]">
            <span className="inline-block h-[7px] w-[7px] rounded-full bg-[color:var(--line-strong)] shrink-0 animate-pulse" />
            Scanning prior traces…
        </span>
    ) : state.status === "error" ? (
        <span className="text-[11px] leading-[1.35] text-[color:var(--muted)]">
            Related work is unavailable. Try again later.
        </span>
    ) : body;

    if (variant === "tab") {
        return (
            <div aria-busy={state.status === "loading"} className="grid gap-[8px] min-w-0">
                {content}
            </div>
        );
    }

    return (
        <div
            aria-busy={state.status === "loading"}
            className="grid gap-[8px] border-l border-[color:var(--line-soft)] pl-[12px] py-[2px] min-w-0"
        >
            <div className="flex items-baseline gap-[8px]">
                <span className={CONTEXT_EYEBROW_CLASS}>Related work</span>
                <HoverTooltip wide placement="top" label={relatedWorkTooltip(state)}>
                    <span className="cursor-help text-[10px] leading-[1.2] text-[color:var(--muted-soft)] underline decoration-dotted underline-offset-2">
                        details
                    </span>
                </HoverTooltip>
            </div>
            {content}
        </div>
    );
}
